import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { UserStateContext } from 'context/user-state-context';
import { DynamicTypeAheadMui } from 'components/dynamic-type-ahead/dynamic-type-ahead-mui.component';
import { snackbarService } from 'components/mui/snackbar/snackbar-service';
import { createTask, getLocations, getUsers } from '../../shared/common.api';
import { StageSelect } from './stage-select.component';
import { BoardSelect } from './board-select.component';
import { useTaskBoards } from './use-task-boards';
import { CategoryMultiSelect } from './category-multiselect.component';
import { CustomerSearchSelect } from './customer-search-select.component';
import { DueDateField } from './due-date-field.component';

// CIN-7073: explain what a blank due date means — the stage the task will land in
// (the picked one, else the backend's default: the first non-terminal stage) may
// carry a default deadline. Returns undefined when there is nothing to say.
const stageDefaultHint = (stages, stageId) => {
  const stage = stages.find(s => s.id === stageId) || stages.find(s => !s.is_terminal);
  const days = stage?.default_due_days;
  if (days === null || days === undefined) return undefined;
  return `Leave blank to use the "${stage.name}" stage default (due in ${days} day${days === 1 ? '' : 's'}).`;
};

// CIN-6288 (R5): a customer's last location is the default visibility location. The
// customer list serializer (CustomerSerializer) exposes the FK as `stat_last_location`
// (id) and the resolved record as `full_location`, so no extra fetch is needed.
// Returns the typeahead's {id, name} seed, or null when the customer has none.
const lastLocationOf = c => {
  const id = c?.stat_last_location || c?.stat_last_location_id || null;
  return id ? {
    id,
    name: c?.full_location?.friendly_name || c?.full_location?.name || 'Last location'
  } : null;
};

/**
 * Modal for creating a new Task Manager task. Wired up via `ModalContext.show`
 * (see task-manager-list.component.js). Resolves with the created task on
 * save; rejects on cancel.
 *
 * Props are injected by ModalContext: `open`, `onClose`, `onSubmit`, `key`.
 *
 * CIN-7070: `initialBoardId` preselects a board (the page's open board); without
 * one the default board (the first) is preselected, so Inbox and customer-drawer
 * launches land there. The Board field only shows when the company has more than
 * one board.
 * CIN-7047: `lockCustomer` locks a prefilled customer without needing an
 * `association` (the customer drawer).
 */
// Reshape the paginated /users response into the flat {..., name} items the
// typeahead renders. Module-scoped so it isn't recreated on every render.
const formatUsers = data => (data?.results || []).map(u => ({
  ...u,
  name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email
}));

// CIN: when launched from an Inbox record, `association` carries the inclusion FK
// (or the text_message source_type) to bind to the new task. Map it to a human
// label for the read-only "Linked to" chip shown above the (locked) customer.
const associationLabel = association => {
  if (!association) return '';
  if (association.inclusion_review) return 'Review';
  if (association.inclusion_survey) return 'Survey';
  if (association.inclusion_schedule) return 'Appointment';
  if (association.inclusion_voice_call) return 'Voice call';
  if (association.source_type === 'text_message') return 'Text message';
  return 'Record';
};
export const TaskCreateModal = ({
  open,
  onClose,
  onSubmit,
  initialCustomer = null,
  association = null,
  initialBoardId = null,
  lockCustomer = false
}) => {
  const {
    asCompany,
    user,
    isCinchEmployee
  } = useContext(UserStateContext);
  const {
    boards
  } = useTaskBoards();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [assignedToId, setAssignedToId] = useState(null);
  const [visibilityLocationId, setVisibilityLocationId] = useState(null);
  // CIN-6288 (R5): {id,name} seed for the location typeahead so an auto-filled
  // (or manually picked) location shows its label right away.
  const [locationItem, setLocationItem] = useState(null);
  const [boardId, setBoardId] = useState(initialBoardId);
  const [stageId, setStageId] = useState(null);
  const [stages, setStages] = useState([]);
  const [categories, setCategories] = useState([]);
  // CIN-7073: optional; null lets the stage's default deadline apply server-side.
  const [dueDate, setDueDate] = useState(null);
  // CIN: pre-bound to the record's customer when launched from the Inbox.
  const [customer, setCustomer] = useState(initialCustomer);
  const [isSaving, setIsSaving] = useState(false);

  // Reset the form whenever the modal (re)opens so a cancelled draft doesn't
  // carry over into the next open. (Reported in review.)
  useEffect(() => {
    if (open) {
      // CIN-7047: a prefilled customer (the drawer, the Inbox) brings their last
      // location, exactly as picking that customer by hand would.
      const initialLocation = lastLocationOf(initialCustomer);
      setName('');
      setDescription('');
      setAssignedToId(null);
      setVisibilityLocationId(initialLocation?.id || null);
      setLocationItem(initialLocation);
      setBoardId(initialBoardId);
      setStageId(null);
      setCategories([]);
      setDueDate(null);
      setCustomer(initialCustomer);
      setIsSaving(false);
    }
  }, [open, initialCustomer, initialBoardId]);

  // CIN-7070: with no board given (or one that no longer exists), preselect the
  // default board — the first in board order, where the server would put the task.
  useEffect(() => {
    if (boards.length && !boards.some(b => b.id === boardId)) setBoardId(boards[0].id);
  }, [boards, boardId]);

  // CIN-6288 (R5): selecting a customer defaults the visibility location to their
  // last location (still editable).
  const handleCustomerChange = c => {
    setCustomer(c);
    const lastLocation = lastLocationOf(c);
    if (lastLocation) {
      setVisibilityLocationId(lastLocation.id);
      setLocationItem(lastLocation);
    }
  };

  // CIN-7070: stages belong to one board, so a new board clears the stage pick.
  const handleBoardChange = id => {
    setBoardId(id);
    setStageId(null);
  };
  const handleSave = () => {
    if (!name.trim()) return;
    setIsSaving(true);
    createTask({
      company: asCompany.id,
      name: name.trim(),
      description: description.trim() || null,
      assigned_to: assignedToId,
      visibility_location: visibilityLocationId || null,
      // CIN-7070: stage null → the server uses the board's first open stage (the
      // default board's when no board is sent).
      board: boardId || null,
      stage: stageId || null,
      categories,
      // CIN-7073: an explicit date wins over the stage default; null = use it.
      due_date: dueDate,
      customer: customer?.id || null,
      // CIN: when launched from an Inbox record, bind the source record (an
      // inclusion FK) and/or source_type so the task links back to it. The
      // backend derives source_type from this and ignores any spoofed value.
      ...(association || {})
    }).then(({
      data
    }) => {
      snackbarService.popup({
        message: `Task "${data.name}" created.`
      });
      onSubmit(data);
    }).catch(error => {
      console.error('Task creation failed:', error);
      snackbarService.popup({
        message: 'Could not create the task. Please try again.',
        type: 'error'
      });
      setIsSaving(false);
    });
  };
  const canSave = !!name.trim() && !isSaving;
  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New Task</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{
        pt: 1
      }}>
          <TextField label="Task name" value={name} onChange={e => setName(e.target.value)} required autoFocus fullWidth />
          <TextField label="Description (optional)" value={description} onChange={e => setDescription(e.target.value)} multiline minRows={2} fullWidth />
          {/* CIN-6288: optionally tie the task to a customer (search by name/email/
              phone — same approach as the reviews "Link to Customer" flow).
              CIN: when launched from an Inbox record the customer is pre-bound and
              the field is locked, so the task, customer, and source record stay
              consistent (a read-only chip shows what it's linked to). */}
          {association && <Chip size="small" label={`Linked to: ${associationLabel(association)}`} sx={{
          alignSelf: 'flex-start',
          borderRadius: '8px'
        }} />}
          <CustomerSearchSelect companyId={asCompany.id} value={customer} onChange={handleCustomerChange} disabled={!!association || lockCustomer} />
          <Box>
            <Typography variant="caption" sx={{
            display: 'block',
            mb: 0.5
          }}>
              Assigned to (optional)
            </Typography>
            <DynamicTypeAheadMui getItems={getUsers} getItemsFilters={{
            company: asCompany.id,
            limit: 999,
            ...(isCinchEmployee ? {} : {
              hide_cinch_users: true
            })
          }} formatApiResults={formatUsers} placeholder="Search teammates" label="Assigned to" displayProperty="name" keyProperty="id" value={assignedToId} onChange={val => setAssignedToId(val ? val.id : null)} />
          </Box>
          <Box>
            <Typography variant="caption" sx={{
            display: 'block',
            mb: 0.5
          }}>
              Visibility location (optional — restrict to Catalyst users at this location)
            </Typography>
            <DynamicTypeAheadMui getItems={getLocations} getItemsFilters={{
            company: asCompany.id,
            limit: 999
          }} placeholder="No location restriction" label="Location" displayProperty="name" keyProperty="id" value={visibilityLocationId} selectedItem={locationItem} onChange={val => {
            setVisibilityLocationId(val ? val.id : null);
            setLocationItem(val ? {
              id: val.id,
              name: val.name
            } : null);
          }} />
          </Box>
          {boards.length > 1 && <BoardSelect boards={boards} value={boardId} onChange={handleBoardChange} />}
          <StageSelect boardId={boardId} value={stageId} onChange={setStageId} onStagesLoaded={setStages} label="Stage (optional — defaults to the first stage)" />
          <CategoryMultiSelect value={categories} onChange={setCategories} />
          <DueDateField value={dueDate} onChange={setDueDate} label="Due date (optional)" helperText={dueDate ? undefined : stageDefaultHint(stages, stageId)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {isSaving ? 'Creating…' : 'Create task'}
        </Button>
      </DialogActions>
    </Dialog>;
};